/**
 * MCP Reminder API Queries
 * React Query hooks for fetching reminders and preferences
 *
 * Gateway mapping: /mcp-reminder/* → mcp-reminder service
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/api/_shared/http';
import { useRoleQueryGate } from '@/api/_shared/roleGate';
import { canUseAdminTenantScope } from '@/utils/authz';
import { noRetryOn403Or400 } from '@/api/_shared/queryDefaults';
import { roleReminderBase } from './paths';
import { reminderKeys } from './keys';
import type { ListRemindersParams, Reminder, ReminderPreference } from './interfaces';

/**
 * WebSocket can stay up while auth is re-loading (e.g. auth:changed).
 * Excludes `loading` so we do not disconnect/reconnect on every token refresh.
 *
 * @returns Whether reminder WebSocket connections may be established
 */
export const useReminderSocketReady = (): boolean => {
  const { payload, role, selectedClientId, isSelectionReady, effectiveClientId } = useRoleQueryGate();

  if (!payload?.userId) return false;
  if (canUseAdminTenantScope(role)) {
    return !!isSelectionReady && !!selectedClientId;
  }
  return !!effectiveClientId;
};

/**
 * Wait for auth + admin client selection (and tenant clientId for non-admin) before reminder API calls.
 *
 * @param enabled - When false, always returns false
 * @returns Whether reminder queries may run
 */
export const useReminderQueriesReady = (enabled = true): boolean => {
  const { ready, role, effectiveClientId } = useRoleQueryGate();

  if (!enabled) return false;
  if (!ready) return false;
  if (!canUseAdminTenantScope(role) && !effectiveClientId) return false;
  return true;
};

/**
 * Fetch reminders for the current role scope (role-aware).
 * - ADMIN: GET /reminder/admin/list with clientId and companyId params
 * - CLIENT: GET /reminder
 *
 * @param params - Optional list filters
 * @param enabled - Whether the query should be enabled
 */
export const useListRemindersQuery = (params?: ListRemindersParams, enabled = true) => {
  const { role, selectedClientId, selectedCompanyId } = useRoleQueryGate();
  const queriesReady = useReminderQueriesReady(enabled);

  return useQuery({
    queryKey: reminderKeys.list(role, selectedClientId, selectedCompanyId, params),
    queryFn: async (): Promise<Reminder[]> => {
      const endpoint = canUseAdminTenantScope(role)
        ? `${roleReminderBase(role)}/list`
        : roleReminderBase(role);
      const reqParams = canUseAdminTenantScope(role)
        ? { ...params, clientId: selectedClientId || undefined, companyId: selectedCompanyId || undefined }
        : params;
      return apiGet<Reminder[]>(endpoint, { params: reqParams });
    },
    enabled: queriesReady,
    retry: noRetryOn403Or400
  });
};

/**
 * Fetch reminder notification preferences for the current role scope (role-aware).
 *
 * @param enabled - Whether the query should be enabled
 */
export const useReminderPreferencesQuery = (enabled = true) => {
  const { role, selectedClientId, selectedCompanyId } = useRoleQueryGate();
  const queriesReady = useReminderQueriesReady(enabled);

  return useQuery({
    queryKey: reminderKeys.preferences(role, selectedClientId, selectedCompanyId),
    queryFn: async (): Promise<ReminderPreference> => {
      const endpoint = `${roleReminderBase(role)}/preferences`;
      const reqParams = canUseAdminTenantScope(role)
        ? { clientId: selectedClientId || undefined, companyId: selectedCompanyId || undefined }
        : undefined;
      return apiGet<ReminderPreference>(endpoint, { params: reqParams });
    },
    enabled: queriesReady,
    retry: noRetryOn403Or400
  });
};
